import i18next from 'i18next';

interface NotificationOptions {
    title: string;
    body?: string;
    icon?: string;
    timestamp: Date;
    reminderMinutes?: number;
}

class NotificationService {
    private static instance: NotificationService;
    private reminders: Map<string, number> = new Map(); // Store timeoutIds

    private constructor() {
        // Private constructor for singleton
    }

    public static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    public async requestPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            console.warn('This browser does not support notifications');
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    public async scheduleReminder(options: NotificationOptions): Promise<string | null> {
        const {
            title,
            body,
            icon,
            timestamp,
            reminderMinutes = 5
        } = options;

        if (!await this.requestPermission()) {
            return null;
        }

        const now = new Date();
        const reminderTime = new Date(timestamp.getTime() - (reminderMinutes * 60 * 1000));

        if (reminderTime <= now) {
            console.warn('Reminder time has already passed');
            return null;
        }

        // Generate a unique ID for this reminder
        const reminderId = `reminder_${timestamp.getTime()}_${Math.random().toString(36).substr(2, 9)}`;

        const timeoutId = window.setTimeout(() => {
            new Notification(title, {
                body,
                icon,
                tag: reminderId // Use the ID as the notification tag
            });
            this.reminders.delete(reminderId);
        }, reminderTime.getTime() - now.getTime());

        this.reminders.set(reminderId, timeoutId);
        return reminderId;
    }

    public cancelReminder(reminderId: string): boolean {
        const timeoutId = this.reminders.get(reminderId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.reminders.delete(reminderId);
            return true;
        }
        return false;
    }

    public hasActiveReminder(timestamp: Date): boolean {
        const reminderPrefix = `reminder_${timestamp.getTime()}`;
        return Array.from(this.reminders.keys()).some(id => id.startsWith(reminderPrefix));
    }

    public clearAllReminders(): void {
        this.reminders.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.reminders.clear();
    }
}

export default NotificationService.getInstance(); 